import React, { useEffect } from 'react';

import FutureThumbnail from '@assets/media/card_next_cover.png';
import DefaultThumbnail from '@assets/media/no_info_thumbnail.png';
import StarIcon from '@assets/media/rating.svg';
import { Translation } from 'common/actions';
import Icon from 'common/components/Icon';
import { Image } from 'common/components/image';
import { Progressbar } from 'common/components/progressbar';
import { Epg } from 'common/constants/data-types';
import { TestableComponent } from 'common/interfaces';
import {
  getDateAndTimeFast,
  getThumbnailTimestampWithOffset,
  getThumbnailUrl
} from 'common/services/helpers';
import { useRadioImage } from 'common/utils/hooks';
import { isEmpty } from 'lodash';
import './Card.styles';

interface CardProps extends TestableComponent {
  i18n: Translation;
  isLive?: boolean;
  hidden?: boolean;
  epg: Epg;
  logo?: string;
  title?: string;
  showDate?: boolean;
  showTime?: boolean;
  showRating?: boolean;
  thumbnailUrl?: string | Promise<string>;
  enableContinueWatching?: boolean;
  focused?: boolean;
}

function Card({
  i18n,
  isLive,
  logo: logoSrc,
  epg,
  hidden,
  title: titleParam,
  thumbnailUrl: thumbnailUrlParam,
  showDate = true,
  showTime = true,
  showRating = true,
  enableContinueWatching = true,
  testId,
  focused = false
}: CardProps) {
  const { start, stop, chan_id, title, rating } = epg;
  const radioImages = useRadioImage(chan_id);
  const realThumbnailUrl = getThumbnailUrl(chan_id, isLive, getThumbnailTimestampWithOffset(start));

  // Show radio image only when the show is not in the future
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | undefined>();

  useEffect(() => {
    const fetchThumbnailUrl = async () => {
      const resolvedThumbnailUrl = thumbnailUrlParam && (await thumbnailUrlParam);
      setThumbnailUrl(
        resolvedThumbnailUrl ||
          (radioImages && realThumbnailUrl !== FutureThumbnail
            ? radioImages.radio_thumbnail_image.url
            : realThumbnailUrl)
      );
    };

    typeof thumbnailUrlParam !== 'string' && fetchThumbnailUrl();
  }, [radioImages, realThumbnailUrl]);

  const parsedDate = getDateAndTimeFast(start, stop, i18n.months);
  const programDate = showDate && `${parsedDate.day} ${parsedDate.month}`;
  const programTimeSlot = showTime && `${parsedDate.start} - ${parsedDate.stop}`;
  const programRating = `${rating?.Overall || rating?.OverallMP || ''}`;
  const ratingVisible = showRating && !isEmpty(programRating);

  if (hidden) {
    return <div className="card-box"></div>;
  }

  return (
    <div className="card-box" data-test-id={testId} data-focused={focused}>
      <Image
        className="card-background"
        src={
          (typeof thumbnailUrlParam === 'string' ? thumbnailUrlParam : thumbnailUrl) || undefined
        }
        fallbackSrc={DefaultThumbnail}
      />
      <div className="card-blurred-overlay" />
      <div className="card-bottom-container">
        {logoSrc && (
          <>
            <Image className="card-logo" src={logoSrc} />
            <span className="card-separator" />
          </>
        )}
        <div className="card-info-container">
          <span className="card-title">{titleParam || title}</span>
          <div className="card-epg-container">
            {showDate && (
              <>
                {programDate}
                <span className="card-epg-separator">·</span>
              </>
            )}
            {showTime && programTimeSlot}
            {ratingVisible && <Icon className="card-rating-icon" size="small" src={StarIcon} />}
            {ratingVisible && programRating}
            {/* Empty span for spacing */}
            {!ratingVisible && <span />}
          </div>
        </div>
      </div>
      {enableContinueWatching && epg.skips?.continue?.progress && (
        <div className="continue-watching-container">
          <Progressbar safeArea={false} active={true} value={epg.skips.continue.progress} />
        </div>
      )}
    </div>
  );
}

export default React.memo(Card);
